import { useEffect, useState } from "react";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";
import { TreeTable } from "primereact/treetable";
import { Column } from "primereact/column";
import "./UploadedFiles.css";
import { fetchFilesByYear, getDownloadUrl } from "../../api/dataService";
import fileValidation from "../../api/fileValidation";
import configDefault from "../../config";
import { fetchAuthSession } from "aws-amplify/auth";
import {
  getShortMonthName,
  formatDateTime,
  formatFileSize,
} from "../../utils/formatters";

const SHOW_ACTIONS = false;
const SHOW_VALIDATION_STATUS = false;

function getYearNodes(config = configDefault) {
  const firstYear = config.FEATURE_FLAGS.DATA.firstYearInDataTable || 2024;
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = currentYear; y >= firstYear; y--) {
    // Descending order
    years.push({
      key: String(y),
      data: { level: "year", name: String(y) },
      leaf: false,
      children: [],
      selectable: false,
    });
  }
  return years;
}

export default function UploadedFiles({ config = configDefault }) {
  const [filesTree, setFilesTree] = useState(getYearNodes(config));
  const [loading, setLoading] = useState(false);

  // Utility function to extract all file keys from the tree structure
  function extractFileKeys(treeData) {
    const fileKeys = [];

    function traverseNode(node) {
      if (node.leaf === true && node.data?.key) {
        fileKeys.push(node.data.key);
      }

      if (node.children && Array.isArray(node.children)) {
        node.children.forEach((child) => traverseNode(child));
      }
    }

    // Handle both single tree node and array of nodes
    if (Array.isArray(treeData)) {
      treeData.forEach((node) => traverseNode(node));
    } else {
      traverseNode(treeData);
    }

    return fileKeys;
  }

  // Handler for expanding a year node
  const onExpand = async (event) => {
    const node = event.node;
    if (
      node.data.level === "year" &&
      (!node.children || node.children.length === 0)
    ) {
      setLoading(true);
      try {
        const session = await fetchAuthSession();
        const token = session.tokens.idToken;
        if (!token) {
          throw new Error("User token is missing. Please log in.");
        }
        const files = await fetchFilesByYear(node.data.name, token); // e.g. "2024"

        // Group files by month and company
        const months = {};
        files.forEach((file) => {
          const date = new Date(file.timestamp);
          const monthNum = date.getMonth() + 1; // 1-12
          if (!months[monthNum]) months[monthNum] = {};
          if (!months[monthNum][file.company])
            months[monthNum][file.company] = [];
          months[monthNum][file.company].push(file);
        });

        // Build month nodes
        const monthNodes = Object.entries(months)
          .sort(([a], [b]) => b - a) // Descending numeric sort
          .map(([monthNum, companies]) => {
            const monthShort = getShortMonthName(Number(monthNum));
            return {
              key: `${node.key}-${monthShort}`,
              data: { level: "month", name: monthShort },
              leaf: false,
              children: Object.entries(companies).map(([company, files]) => ({
                key: `${node.key}-${monthShort}-${company}`,
                data: { level: "company", name: company },
                leaf: false,
                children: files.map((file) => ({
                  key: `${node.key}-${monthShort}-${company}-${file.fileName}`,
                  data: { ...file, level: "file" },
                  leaf: true,
                })),
              })),
            };
          });

        // Update tree
        const updatedTree = filesTree.map((y) =>
          y.key === node.key ? { ...y, children: monthNodes } : y
        );

        setFilesTree(updatedTree);

        // Extract file keys from the updated tree (not the old filesTree state)
        const fileKeys = extractFileKeys(updatedTree);

        const payload = {
          files: fileKeys,
        };

        // Call the validation service using the imported function
        const response = await fileValidation.post(
          process.env.REACT_APP_FILE_VALIDATION_LAMBDA_PATH,
          payload,
          token
        );

        // console.log("Validation response:", response);
        if (response && response.data) {
          // Access the results data from the correct location in the response structure
          const validationResults = Array.isArray(response.data.results)
            ? response.data.results
            : [];

          if (validationResults.length > 0) {
            // Create a map of key to validation_status for easy lookup
            const statusMap = {};
            const validationKey = {};
            validationResults.forEach((result) => {
              // console.log("Validation result:", result);
              statusMap[result.fileName] = result.label;
              if (result.label === "Failed") {
                validationKey[result.fileName] =
                  result.metadata?.error_report_key;
              }
            });

            // Update filesTree with validation status
            setFilesTree((prevTree) => {
              const updateValidationStatus = (nodes) => {
                return nodes.map((node) => {
                  if (
                    node.leaf === true &&
                    node.data?.key &&
                    statusMap[node.data.key]
                  ) {
                    const nodeData = {
                      ...node.data,
                      validationStatus: statusMap[node.data.key],
                    };

                    // Add feedbackKey if status is "Failed"
                    if (statusMap[node.data.key] === "Failed") {
                      nodeData.feedbackKey = validationKey[node.data.key];
                    }

                    return {
                      ...node,
                      data: nodeData,
                    };
                  }

                  if (node.children && Array.isArray(node.children)) {
                    return {
                      ...node,
                      children: updateValidationStatus(node.children),
                    };
                  }

                  return node;
                });
              };

              return updateValidationStatus(prevTree);
            });
          }
        }
      } catch (error) {
        console.error("Error fetching files by year:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const renderValidationStatus = (rowData) => {
    const { validationStatus, fileName } = rowData.data;
    // console.log("row data:", rowData.data);
    if (!validationStatus) return null;

    const isFailed = validationStatus === "Failed";
    let url = null;
    const handleDownload = async (e) => {
      e.preventDefault();
      try {
        const session = await fetchAuthSession();
        const token = session.tokens.idToken;

        const fileKey = rowData.data?.feedbackKey;

        const url = await getDownloadUrl(fileKey, token);

        // Create and trigger download using the feedbackUrl
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName || "feedback.txt";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (err) {
        alert("Failed to download file.");
        console.error(err);
      }
    };

    return (
      <div
        className={`status-badge-container`}
        style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
      >
        <span
          className={`status-badge ${
            validationStatus === "In Progress"
              ? "in-progress"
              : validationStatus === "Failed"
              ? "failed"
              : "passed"
          }`}
        >
          {validationStatus}
        </span>
        {isFailed && (
          <>
            <a
              href={url}
              download
              className="feedback-download-link"
              style={{ color: "#007ad9", cursor: "pointer" }}
              onClick={handleDownload}
              data-pr-tooltip="Download validation feedback"
              data-pr-position="right"
            >
              <i className="pi pi-download" />
            </a>
            <Tooltip target=".feedback-download-link" />
          </>
        )}
      </div>
    );
  };

  const renderActions = (rowData) => {
    // Only show actions for rows where the level is "file"
    if (rowData.data.level !== "file") {
      return null;
    }

    return (
      <div className="action-buttons">
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text p-button-danger"
          onClick={() => console.log(`Deleting ${rowData.data.fileName}`)}
        />
      </div>
    );
  };

  const renderFileName = (rowData) => {
    if (rowData.data.level === "file") {
      const originalFileName = rowData.data.metadata?.originalfilename
        ? decodeURIComponent(rowData.data.metadata.originalfilename)
        : rowData.data.fileName;

      const handleDownload = async (e) => {
        e.preventDefault();
        try {
          const session = await fetchAuthSession();
          const token = session.tokens.idToken;
          const fileKey = rowData.data?.key;
          if (!fileKey) {
            alert("File key is missing. Cannot download this file.");
            return;
          }
          const url = await getDownloadUrl(fileKey, token);
          const link = document.createElement("a");
          link.href = url;
          link.download = rowData.data.fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } catch (err) {
          alert("Failed to download file.");
          console.error(err);
        }
      };

      return (
        <div className="flex align-items-center">
          <button
            type="button"
            onClick={handleDownload}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              margin: 0,
              textDecoration: "underline",
              color: "#007ad9",
              cursor: "pointer",
              font: "inherit",
              textAlign: "left", // Add this line
            }}
            aria-label={`Download ${rowData.data.fileName}`}
          >
            {rowData.data.fileName}
          </button>
          <i
            className="pi pi-info-circle info-icon"
            style={{ marginLeft: "0.5rem", cursor: "pointer" }}
            data-pr-tooltip={`Original file name: ${originalFileName}`}
            data-pr-position="right"
          />
          <Tooltip target=".info-icon" />
        </div>
      );
    }
    return null;
  };

  return (
    <Card
      title="Uploaded Files"
      style={{ border: "none", boxShadow: "none", padding: "0.75rem" }}
    >
      <TreeTable
        value={filesTree}
        loading={loading}
        onExpand={onExpand}
        paginator
        rows={5}
        emptyMessage="No files available."
      >
        <Column
          field="name"
          header="Hierarchy"
          expander
          style={{ width: "20%" }}
        />
        <Column
          body={renderFileName}
          header="File Name"
          style={{ width: "45%" }}
        />
        <Column
          field="timestamp"
          header="Timestamp"
          body={(rowData) =>
            rowData.data.level === "file"
              ? formatDateTime(rowData.data.timestamp)
              : ""
          }
          style={{ width: "25%" }}
        />
        {SHOW_VALIDATION_STATUS && (
        <Column
          body={renderValidationStatus}
          header="Validation Status"
          style={{ width: "15%" }} // Increased width for better visibility
        />
        )}
        <Column
          field="size"
          header="Size"
          body={(rowData) =>
            rowData.data.level === "file"
              ? formatFileSize(rowData.data.size)
              : ""
          }
          style={{ width: "10%" }}
        />
        {SHOW_ACTIONS && (
          <Column
            body={renderActions}
            header="Actions"
            style={{ width: "15%" }}
          />
        )}
      </TreeTable>
    </Card>
  );
}
